import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react-lite';

import Question from '../components/Question';
import MessagePropType from '../definitions/MessagePropType';
import useSession from '../hooks/useSessionStore';


function QuestionEmailAddress({
    name,
    value,
    fieldOptions,
    labelMessageVariant,
    labelMessage,
    children,
    isEditableOnSignIn,
    ...otherProps
}) {
    const sessionStore = useSession();

    const isSignInLocked = sessionStore.hasFetchedSession
        ? sessionStore.isLoggedIn && !isEditableOnSignIn
        : !isEditableOnSignIn; // we don't know yet if user signed in or not

    return (
        <Question
            field={{
                name,
                // If verified user, always use their sessionStore email
                value: isSignInLocked ? sessionStore.email : value,
                type: 'email',
                ...fieldOptions,
                disabled: fieldOptions?.disabled || isSignInLocked,
            }}
            labelMessage={labelMessage ?? getDefaultMessage(labelMessageVariant)}
            {...otherProps}
        >
            {children}
        </Question>
    );
}

const DEFAULT_MESSAGE_STYLES = [ 'brief', 'conversational' ];

QuestionEmailAddress.propTypes = {
    name: PropTypes.string,
    value: PropTypes.string,
    fieldOptions: PropTypes.shape({
        required: PropTypes.bool,
        disabled: PropTypes.bool,
        validators: PropTypes.arrayOf(PropTypes.object),
    }),
    isEditableOnSignIn : PropTypes.bool,
    labelMessageVariant: PropTypes.oneOf(DEFAULT_MESSAGE_STYLES),
    labelMessage: MessagePropType,
    children: PropTypes.node,
};

QuestionEmailAddress.defaultProps = {
    name: 'email',
    value: undefined,
    fieldOptions: undefined,
    labelMessageVariant: 'brief',
    labelMessage: undefined,
    children: undefined,
    isEditableOnSignIn: false,
};


const DEFAULT_MESSAGES = {
    brief: (
        <FormattedMessage
            id="base-ui.email-address.label.brief"
            defaultMessage="Email address"
        />
    ),
    conversational: (
        <FormattedMessage
            id="base-ui.email-address.label.conversational"
            defaultMessage="What is your email address?"
        />
    ),
};

function getDefaultMessage(messageStyle) {
    if (!DEFAULT_MESSAGES[messageStyle]) {
        throw new RangeError(`QuestionEmailAddress -- unexpected message style value “${messageStyle}”`);
    }

    return DEFAULT_MESSAGES[messageStyle];
}

export default observer(QuestionEmailAddress);
